import type { TabData } from '@/__core/types/TabData';

const normalizeCompanyId = (value: unknown): string => {
  if (value == null) return '';
  return String(value).trim();
};

export interface ResolveEntityCompanyIdInput {
  company_id?: unknown;
  company?: unknown;
}

/** Extract company UUID/string from API entity payloads. */
export function resolveEntityCompanyId(
  entity?: ResolveEntityCompanyIdInput | null
): string | null {
  if (!entity) return null;

  const direct = normalizeCompanyId(entity.company_id);
  if (direct) return direct;

  const company = entity.company;
  if (company && typeof company === 'object') {
    const nested = normalizeCompanyId(
      (company as { id?: unknown; company_id?: unknown }).id
        ?? (company as { company_id?: unknown }).company_id
    );
    if (nested) return nested;
  }

  if (typeof company === 'string') {
    const asString = normalizeCompanyId(company);
    if (asString) return asString;
  }

  return null;
}

/**
 * Resolve the company a tab belongs to (for toolbar visibility filtering).
 */
export function resolveTabCompanyId(tab: TabData): string | null {
  const metadata = tab.metadata;

  const fromMetadata = normalizeCompanyId(metadata?.companyId);
  if (fromMetadata) return fromMetadata;

  const formData = metadata?.formData;
  if (formData && typeof formData === 'object') {
    const fromFormCompany = normalizeCompanyId(
      (formData as { company?: unknown }).company
        ?? (formData as { company_id?: unknown }).company_id
    );
    if (fromFormCompany) return fromFormCompany;
  }

  const entityData = tab.entityData;
  if (entityData && typeof entityData === 'object') {
    const fromEntity = resolveEntityCompanyId(
      entityData as ResolveEntityCompanyIdInput
    );
    if (fromEntity) return fromEntity;
  }

  return null;
}

/**
 * Whether a tab should appear when a toolbar company filter is active.
 * List tab is always visible. Create tabs (`add`, `new-*`) and edit tabs are scoped
 * to `metadata.companyId` / entity company — hidden when toolbar company differs.
 */
export function isTabVisibleForToolbarCompany(
  tab: TabData,
  activeCompanyFilter?: string | null
): boolean {
  if (
    tab.type === 'list' ||
    (typeof tab.type === 'string' && tab.type.endsWith('-list'))
  ) {
    return true;
  }

  const filter = normalizeCompanyId(activeCompanyFilter);
  if (!filter || filter === 'all') {
    return true;
  }

  const tabCompanyId = resolveTabCompanyId(tab);
  if (!tabCompanyId) {
    return false;
  }

  return tabCompanyId === filter;
}

export function filterTabsByToolbarCompany(
  tabs: TabData[],
  activeCompanyFilter?: string | null
): TabData[] {
  return tabs.filter((tab) => isTabVisibleForToolbarCompany(tab, activeCompanyFilter));
}
